import { call, put, select, takeEvery } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import { RootState } from '../reducers/rootReducer';
import { getOrders, getRecallOrders, setOrders, setOrderStatus } from '../actions/orders.actions';
import { setLoading } from '../actions/loading.actions';
import { mapGetOrdersResponseToSetOrdersPayload } from './getOrders.mappers';

import {
  BFF_ORDERS,
  BFF_STORES,
  SAGA_GET_ORDERS,
  VERIFIED_ORDERS,
  COMMERCIAL_CUSTOMER_SERVICE_DOWN,
  ORDER_SERVICE_ERROR,
  ORDERS_API_ERROR,
  BFF_V2,
  ORDERS_OPENED,
  ORDERS_REFRESH,
  ORDERS_INITIALIZED,
  ORDERS_DATA_REFRESHED,
  PICKING_APIGEE_BASE_PATH,
} from '../../constants';

import { GetOrdersPayload, SetOrdersPayload } from '../actions/orders.types';
import { AppLogType, ConfigAuthValues, Order, APIError } from '../../types';

import { callAPI, determineDeviceType, isCommercialOrder, stripLeadingZeroes, createApiResponseTimeHelper } from '../../utils';

import { handleApiErrors } from './utils';

import { removeRecallOrders } from '../../recallStorageUtils';

import { OrdersState } from '../reducers/types';
import dingSound from '../../assets/audio/ding.mp3';
import eoSound from '../../assets/audio/acsyhaow.mp3';
import { sendLog } from '../actions/log.actions';
import { APIPickTour, GetOrdersResponse } from './types';

const callGetOrders = (orderCallData: GetOrdersPayload, configValues: ConfigAuthValues) => {
  return callAPI({
    method: 'GET',
    url: `${configValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V2}${BFF_STORES}/${orderCallData.fulfillmentStoreID}${BFF_ORDERS}`,
  });
};

export function* runGetOrdersSaga(action: { type: string; payload: GetOrdersPayload }): Generator<any, void, any> {
  yield put(setLoading({ sagaName: SAGA_GET_ORDERS, isLoading: true }));
  
  const getApiResponseTime = createApiResponseTimeHelper();
  
  const configValues = yield select((state: RootState) => state.config);
  const ordersState: OrdersState = yield select((state: RootState) => state.orders);
  const ding = new Audio(dingSound);
  const eo = new Audio(eoSound);

  const currentOrdersInState: Order[] = Object.values(ordersState.ordersByType || {}).reduce((acc: Order[], group) => {
    if (group && group.orders) {
      acc.push(...group.orders);
    }
    return acc;
  }, []);

  // Check flags separately for each event type 
  const hasLoggedOrdersOpened = sessionStorage.getItem(ORDERS_OPENED);
  const hasLoggedOrdersRefresh = sessionStorage.getItem(ORDERS_REFRESH);
  const isFirstTime = !currentOrdersInState.length;

  try {
    let apiResponseData: GetOrdersResponse;
    let apiResponseTime = 0;
    const storedData = localStorage.getItem(VERIFIED_ORDERS);

    if (storedData) {
      try {
        const parsedCachedData: GetOrdersResponse = JSON.parse(storedData);

        if (
          parsedCachedData &&
          typeof parsedCachedData === 'object' &&
          'ordersByType' in parsedCachedData &&
          'errors' in parsedCachedData
        ) {
          apiResponseData = parsedCachedData;
          localStorage.removeItem(VERIFIED_ORDERS);
          apiResponseTime = getApiResponseTime();
        } else {
          console.warn('Cached orders in localStorage are malformed. Fetching fresh data.');
          localStorage.removeItem(VERIFIED_ORDERS);
          const result = yield call(
            callGetOrders,
            { fulfillmentStoreID: action.payload.fulfillmentStoreID },
            configValues.authValues
          );
          apiResponseTime = getApiResponseTime();
          apiResponseData = result.data;
        }
      } catch (parseError) {
        console.error('Error parsing cached orders from localStorage, fetching fresh data:', parseError);
        localStorage.removeItem(VERIFIED_ORDERS);
        const result = yield call(
          callGetOrders,
          { fulfillmentStoreID: action.payload.fulfillmentStoreID },
          configValues.authValues
        );
        apiResponseTime = getApiResponseTime();
        apiResponseData = result.data;
      }
    } else {
      const result = yield call(
        callGetOrders,
        {
          fulfillmentStoreID: action.payload.fulfillmentStoreID,
        },
        configValues.authValues
      );
      apiResponseTime = getApiResponseTime();
      apiResponseData = result.data;
    }

    yield put(setOrderStatus({ lastAttemptSuccess: true, lastAttemptTimeStamp: Date.now() }));

    const allPickToursFromResponse: APIPickTour[] = Object.values(apiResponseData.ordersByType || {}).reduce(
      (acc: APIPickTour[], group) => {
        return acc.concat(group?.pickTours || []);
      },
      []
    );

    const pickTourOrderIds = allPickToursFromResponse.map(tour =>
      tour.metadata.ordersSummaryData.map(order => order.orderID)
    );
    const flattenedIds = pickTourOrderIds.reduce((acc, curVal) => {
      return [...acc, ...curVal];
    }, []);
    const localStorageRecallData = localStorage.getItem('storeRecallData');
    if (localStorageRecallData) {
      const parsedData = JSON.parse(localStorageRecallData);
      const currentStoreData = parsedData[stripLeadingZeroes(action.payload.fulfillmentStoreID)];
      if (currentStoreData) {
        const currentStoreRecallOrders = currentStoreData.recallOrders;
        const recallIdsToRemove: string[] = currentStoreRecallOrders
          .filter((recallOrder: Order) => flattenedIds.includes(recallOrder.id))
          .map((order: Order) => order.id);
        if (recallIdsToRemove.length > 0) {
          removeRecallOrders(recallIdsToRemove, action.payload.fulfillmentStoreID);
        }
      }
    }

    const deviceType = determineDeviceType();
    const mappedPayload: SetOrdersPayload = mapGetOrdersResponseToSetOrdersPayload(apiResponseData);

    // Calculate the actual number of orders retrieved from the API
    const ordersFromResponse: Order[] = Object.values(mappedPayload.ordersByType || {}).reduce(
      (acc: Order[], group) => {
        if (group && group.orders) {
          acc.push(...group.orders);
        }
        return acc;
      },
      []
    );

    if (isFirstTime && !hasLoggedOrdersOpened) {
      yield put(sendLog({
          logEvent: AppLogType.ORDERS_OPENED,
          logMessage: ORDERS_INITIALIZED,
          logDetails: {
            ordersRetrieved: ordersFromResponse.length,
            apiResponseTime,
          },
      }));
      sessionStorage.setItem(ORDERS_OPENED, 'true');
    } else if (!isFirstTime && !hasLoggedOrdersRefresh) {
      yield put(sendLog({
          logEvent: AppLogType.ORDERS_REFRESH,
          logMessage: ORDERS_DATA_REFRESHED,
          logDetails: {
            ordersRetrieved: ordersFromResponse.length,
            apiResponseTime,
          },
      }));
      sessionStorage.setItem(ORDERS_REFRESH, 'true');
    }

    if (action.payload.refreshOrders) {
      const newOrdersFromPayload: Order[] = Object.values(mappedPayload.ordersByType || {}).reduce(
        (acc: Order[], group) => {
          if (group && group.orders) {
            acc.push(...group.orders);
          }
          return acc;
        },
        []
      );
      if (deviceType !== 'Desktop') {
        const prevCommercialOrderIDs = currentOrdersInState
          .filter(order => isCommercialOrder(order.type))
          .map(order => order.id);
        if (
          newOrdersFromPayload.some(
            order => isCommercialOrder(order.type) && !prevCommercialOrderIDs.includes(order.id)
          )
        ) {
          ding.play();
        }
      } else if (newOrdersFromPayload.some(order => isCommercialOrder(order.type))) {
        eo.play();
      }
    }

    // TODO: Validate API response
    yield put(setOrders(mappedPayload));
    if (configValues.recallPickingEnabled) {
      yield put(getRecallOrders({ fulfillmentStoreID: action.payload.fulfillmentStoreID }));
    }

    if (apiResponseData.errors && apiResponseData.errors.length > 0) {
      const filteredErrors = apiResponseData.errors.filter(
        (err: APIError) =>
          !(
            err.code === COMMERCIAL_CUSTOMER_SERVICE_DOWN &&
            err.detail.toLowerCase().includes('unable to check UPC validation flag'.toLowerCase())
          )
      );
      yield handleApiErrors(filteredErrors);
    } else if (apiResponseData.errors) {
      yield handleApiErrors(apiResponseData.errors);
    }
  } catch (error) {
    const apiResponseTime = getApiResponseTime();

    if ((error as any)?.response?.status === 401) {
      console.log(ORDERS_API_ERROR, error);
    }
    yield put(setOrderStatus({ lastAttemptSuccess: false, lastAttemptTimeStamp: Date.now() }));
    yield put(
      setOrders({
        ordersByType: {},
      })
    );

    const orderError: APIError[] = [
      { id: '', code: ORDER_SERVICE_ERROR, detail: JSON.stringify(error || 'Unknown error during API call') },
    ];

    yield handleApiErrors(
      orderError,
      {
        saga: 'getOrders',
        method: 'GET',
        url: configValues?.authValues
          ? `${configValues.authValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V2}${BFF_STORES}/${
              action.payload.fulfillmentStoreID
            }${BFF_ORDERS}`
          : 'unknown',
        config: configValues?.authValues,
      },
      apiResponseTime
    );
  }

  yield put(setLoading({ sagaName: SAGA_GET_ORDERS, isLoading: false }));
}

export default function* watchGetOrders() {
  yield takeEvery(getType(getOrders), runGetOrdersSaga);
}
